import { ClipboardList, Users, Menu, House } from "lucide-react";
import DiaryPenIcon from "@/components/shared/DiaryPenIcon";
import { useLocation, useNavigate } from "react-router";
import type React from "react";
import { DIARY_HOME_LABEL } from "@/hooks/useMyPageLabel";
import { useHome } from "@/hooks/useHome";
import { useOnboarding } from "@/hooks/useOnboarding";
import { path } from "@/utils/common";
import { withNavJump } from "@/lib/nav/backTracker";
import {
  ROUTE_MEETINGS_LIST,
  ROUTE_MY_CUSTOMERS,
  ROUTE_MYPAGE,
} from "@/utils/const";

/**
 * 下部ナビ（PRODUCT_DEV-583 → PRODUCT_DEV-782）。**4タブ**（マイ手帳 / 商談一覧 / 顧客一覧 / メニュー）で、
 * **先頭が手帳**。アプリを開いて最初に出るのが手帳、という 2d案の思想をナビでも通すため。
 *
 * PRODUCT_DEV-949: タブ名を「商談記録」→「商談一覧」に変え、アイコンを家（ホームの名残り）から
 * クリップボードの一覧に、手帳を開いた本から「分厚い手帳＋筆ペン」（`DiaryPenIcon`）に替えた。
 * 顧客一覧（人）・手帳（本）と形が被らないこと、吹き出しを使わないことが条件。
 *
 * ⚠ **商談一覧の URL（`/`）は動かさない。** 絞り込みを URL に載せる作りで、通知・共有リンク・
 * ブックマークがこの URL を指している。ホームの入れ替えはタブの並びと着地先で行う。
 *
 * ⚠ 「検索」は商談一覧の「閲覧 / 保存」ピル行から、「録音（議事録・メモ）」は手帳のメモタイルの
 * 「すべて見る」から入れる（タブには置かない）。
 *
 * PRODUCT_DEV-1092: マネージャー画面への切替は**ここには置かない**（名前の行の右端。`ViewIdentityHeader`）。
 * 10/8 の版は左端に「⇄ マネージャー画面」を足して5つにしていたが、左端は HOME に戻る場所として
 * 手が覚えていて押し間違えるので外した（2026-10-13 ユーザ決定）。マネージャー画面側のナビは `ManagerTabBar`。
 */
interface Ph180TabBarProps {
  onMenuClick: () => void;
  isSearchOpen?: boolean;
  onCloseSearch?: () => void;
}

const HOME_PATH = path("/");

const Ph180TabBar: React.FC<Ph180TabBarProps> = ({
  onMenuClick,
  isSearchOpen,
  onCloseSearch,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // 下のタブからの移動は「← はひとつ前の画面」にする印を付ける（PRODUCT_DEV-1159）
  const go = (to: string) => navigate(to, { state: withNavJump() });
  // PRODUCT_DEV-1070: HOME のタブに小さな家を添える
  const home = useHome();
  const homeTabKey =
    home.screen === "diary"
      ? "diary"
      : home.screen === "meetings"
        ? "home"
        : null; // 活動サマリはマネージャー画面のナビ（ManagerTabBar）に家を付ける
  // PRODUCT_DEV-783: メニューの「はじめての方へ」を1度開くまで、メニューのタブにも赤ポチ（行が見えないため）
  const { showNewDot: onboardingDot } = useOnboarding();

  const onMyCustomers = pathname.startsWith(ROUTE_MY_CUSTOMERS);
  const onMyPage = pathname.startsWith(ROUTE_MYPAGE);

  // 検索オーバーレイが開いている間に他タブをタップしたら、まず overlay を閉じる。
  const wrapWithCloseSearch = (action: () => void) => () => {
    if (isSearchOpen) onCloseSearch?.();
    action();
  };

  type Tab = {
    key: "home" | "mycustomers" | "menu" | "diary";
    label: string;
    icon: typeof ClipboardList;
    active: boolean;
    onClick: () => void;
  };

  /** 手帳（PRODUCT_DEV-782）。先頭に置く。 */
  const diaryTab: Tab = {
    key: "diary",
    label: DIARY_HOME_LABEL,
    icon: DiaryPenIcon,
    active: onMyPage,
    onClick: wrapWithCloseSearch(() => go(ROUTE_MYPAGE)),
  };

  const tabs: Tab[] = [
    diaryTab,
    {
      key: "home",
      label: "記録一覧",
      icon: ClipboardList,
      // `/` は手帳へ送られるので、一覧に必ず当たる URL を指す
      active: pathname === ROUTE_MEETINGS_LIST || pathname === HOME_PATH,
      onClick: wrapWithCloseSearch(() => go(ROUTE_MEETINGS_LIST)),
    },
    {
      key: "mycustomers",
      label: "顧客一覧",
      icon: Users,
      active: onMyCustomers,
      onClick: wrapWithCloseSearch(() => go(ROUTE_MY_CUSTOMERS)),
    },
    {
      key: "menu",
      label: "メニュー",
      icon: Menu,
      active: false,
      onClick: wrapWithCloseSearch(onMenuClick),
    },
  ];

  // PRODUCT_DEV-981: bottom-0 ではなく fixed-bottom-bar（Android でスクロールするとナビバーの裏へ下がるため。index.css）
  return (
    <nav className="fixed-bottom-bar left-0 right-0 bg-white border-t border-neutral-200 z-40">
      <div className="grid h-16 grid-cols-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const color = tab.active ? "text-primary" : "text-neutral-500";
          const weight = tab.active ? "font-bold" : "font-normal";
          return (
            <button
              key={tab.key}
              onClick={tab.onClick}
              className="flex flex-col items-center justify-center gap-0.5 transition-colors hover:text-neutral-700"
            >
              <span className="relative">
                <Icon size={22} className={color} strokeWidth={tab.active ? 2.25 : 2} />
                {tab.key === "menu" && onboardingDot && (
                  <span
                    className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-danger ring-2 ring-white"
                    aria-label="新しい案内があります"
                    role="status"
                  />
                )}
              </span>
              {/* PRODUCT_DEV-1070: HOME の印。家はラベルの外（左）にはみ出させ、
                  付いていてもいなくてもラベルがアイコンの真下からずれないようにする */}
              <span className={`relative text-[10px] ${color} ${weight}`}>
                {tab.key === homeTabKey && (
                  <House
                    size={10}
                    strokeWidth={2.5}
                    aria-hidden
                    className="absolute right-full top-1/2 -translate-y-1/2 mr-0.5 text-neutral-400"
                  />
                )}
                {tab.label}
                {tab.key === homeTabKey && <span className="sr-only">（HOME）</span>}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default Ph180TabBar;
